// ** Third Party Components
import styled, { css } from "styled-components";
import Tooltip from "components/layout/Tooltip";

// ** Styles Import
import { breakpoints } from "styles/grid";
import DynamicHeartIcon from "components/icons/DynamicHeartIcon";
import DynamicCommentIcon from "components/icons/DynamicCommentIcon";
import { TrashIconCustom } from "components/icons/TrashIcon";
import Block from "./Block";
import { Icon } from "components/icons";

const StyledActionButton = styled.button`
	position: relative;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	align-self: start;
	margin: 0 0 0 0.5rem;
	padding: 0 0.6rem;
	height: ${({ isSmall }) => (isSmall ? "25px" : "35px")};
	background-color: transparent;
	box-shadow: none;
	border-radius: ${({ theme: { borderRadius }, isSmall }) =>
		isSmall ? "0.9rem" : borderRadius.card};

	cursor: pointer !important;
	${({ customMargin }) => {
		return (
			customMargin &&
			css`
				margin: ${customMargin};
			`
		);
	}}

	color: ${({ theme: { colors } }) => colors.black};
	svg {
		fill: ${({ theme: { colors } }) => colors.black};
	}

	@media (max-width: ${breakpoints.phone - 1}px) {
		&:hover {
			svg {
				fill: ${({ theme: { colors } }) => colors.black};
			}
		}
	}

	&:hover {
		background-color: ${({ noHover, theme: { colors } }) =>
			noHover ? "transparent" : colors.itemsHover}!important;
	}

	@media (max-width: ${breakpoints.phone - 1}px) {
		&:not(:disabled):hover {
			background-color: transparent;
		}
	}

	&:not(:disabled) {
		background-color: transparent;
	}

	&:not(:disabled):active {
		background-color: transparent;
	}

	${({ isHeart }) => {
		return (
			isHeart &&
			css`
				&:hover {
					svg {
						fill: ${({ theme: { colors } }) => colors.red};
					}
				}
				${({ liked }) =>
					liked &&
					css`
						svg {
							fill: ${({ theme: { colors } }) => colors.red};
						}
						&:hover {
							svg {
								fill: ${({ theme: { colors } }) =>
									colors.redOnHover};
							}
						}
					`}
			`
		);
	}}

	${({ isComment }) => {
		return (
			isComment &&
			css`
				&:hover {
					svg {
						fill: ${({ theme: { colors, isDarkMode } }) =>
							isDarkMode ? colors.primaryLight : colors.primary};
					}
				}

				@media (max-width: ${breakpoints.phone - 1}px) {
					&:hover {
						svg {
							fill: ${({ theme: { colors } }) => colors.black};
						}
					}
				}

				${({ commented }) =>
					commented &&
					css`
						svg {
							fill: ${({ theme: { colors, isDarkMode } }) =>
								isDarkMode
									? colors.primaryLight
									: colors.primary};
						}
						&:hover {
							svg {
								fill: ${({ theme: { isDarkMode } }) =>
									isDarkMode
										? "var(--dz-ref-color-primaryLight400)"
										: "var(--dz-ref-color-primary400)"};
							}
						}
					`}

				${({ isActive }) =>
					isActive &&
					css`
						background-color: ${({ theme: { colors } }) =>
							colors.itemsHover}!important;
						&:hover {
							background-color: ${({ theme: { colors } }) =>
								colors.itemsHover}!important;
						}
					`}
			`
		);
	}}

${({ isPen }) => {
		return (
			isPen &&
			css`
				&:hover {
					svg {
						fill: ${({ theme: { colors, isDarkMode } }) =>
							isDarkMode ? colors.primaryLight : colors.primary};
					}
				}

				@media (max-width: ${breakpoints.phone - 1}px) {
					&:hover {
						svg {
							fill: ${({ theme: { colors } }) => colors.black};
						}
					}
				}
			`
		);
	}}

${({ isTrash }) => {
		return (
			isTrash &&
			css`
				&:hover {
					svg {
						fill: ${({ theme: { colors, isDarkMode } }) =>
							isDarkMode ? colors.primaryLight : colors.primary};
					}
				}

				@media (max-width: ${breakpoints.phone - 1}px) {
					&:hover {
						svg {
							fill: ${({ theme: { colors } }) => colors.black};
						}
					}
				}
			`
		);
	}}


&:disabled {
		opacity: var(--dz-sys-button-disabled-opacity);
	}

	${({ disabled, noColorDisabled }) => {
		if (disabled && noColorDisabled) {
			return "";
		} else if (disabled) {
			return css`
				background-color: transparent;
				svg {
					fill: ${({ theme: { colors } }) => colors.grey}!important;
				}

				&:hover {
					svg {
						fill: ${({ theme: { colors } }) =>
							colors.grey}!important;
					}
				}
			`;
		}
	}}

	${({ alwaysDark }) => {
		return (
			alwaysDark &&
			css`
				color: hsl(0, 0%, 95%);
				svg {
					fill: hsl(0, 0%, 95%);
				}

				&:hover {
					background-color: ${({ noHover }) =>
						noHover ? "transparent" : "#373737"}!important;
				}

				${({ isComment }) => {
					return (
						isComment &&
						css`
							&:hover {
								svg {
									fill: hsl(0, 0%, 95%);
								}
							}

							${({ commented }) =>
								commented &&
								css`
									svg {
										fill: #568dfd;
									}
									&:hover {
										svg {
											fill: #6d9cfd;
										}
									}
								`}

							${({ isActive }) =>
								isActive &&
								css`
									background-color: #373737 !important;
									&:hover {
										background-color: #373737 !important;
									}
								`}
						`
					);
				}}

				${({ isHeart }) => {
					return (
						isHeart &&
						css`
							padding: 0 0.6rem 0 0;
							&:hover {
								svg {
									fill: ${({ theme: { colors } }) =>
										colors.red};
								}
							}
							${({ liked }) =>
								liked &&
								css`
									svg {
										fill: ${({ theme: { colors } }) =>
											colors.red};
									}
									&:hover {
										svg {
											fill: ${({ theme: { colors } }) =>
												colors.redOnHover};
										}
									}
								`}
						`
					);
				}}

				&:disabled {
					opacity: var(--dz-sys-button-disabled-opacity);
				}

				${({ disabled, noColorDisabled }) => {
					if (disabled && noColorDisabled) {
						return "";
					} else if (disabled) {
						return css`
							background-color: transparent;
							svg {
								fill: #a9a9a9 !important;
							}

							&:hover {
								svg {
									fill: #a9a9a9 !important;
								}
							}
						`;
					}
				}}
			`
		);
	}}
`;

export const ActionButton = ({
	isHeart,
	isComment,
	isPen,
	isTrash,
	onClick,
	disabled = false,
	noColorDisabled,
	commented,
	liked,
	label = null,
	labelSize = "14px",
	customSvgSize,
	customMargin,
	tooltipInfo,
	tooltipPlacement,
	alwaysDark = false,
	isActive = false,
	noHover = false,
}) => {
	if (disabled) {
		return (
			<Tooltip
				text={disabled ? tooltipInfo : ""}
				placement={tooltipPlacement}
				wrapperStyle={{
					position: "relative",
					left: 0,
					right: 0,
					top: 0,
					bottom: 0,
				}}
			>
				<StyledActionButton
					alwaysDark={alwaysDark}
					onClick={onClick}
					isHeart={isHeart}
					isComment={isComment}
					isTrash={isTrash}
					isPen={isPen}
					disabled={disabled}
					noColorDisabled={noColorDisabled}
					commented={commented}
					liked={liked}
					customMargin={customMargin}
					isSmall={
						customSvgSize === "13px" ||
						customSvgSize === "14px" ||
						customSvgSize === "15px"
					}
				>
					<Block flex alignCenter relative>
						{isHeart && (
							<DynamicHeartIcon
								height={customSvgSize}
								solid={liked}
							/>
						)}
						{isComment && (
							<DynamicCommentIcon
								height={customSvgSize}
								solid={commented}
							/>
						)}
						{isPen && (
							<Icon
								name="pen"
								variant="regular"
								height={customSvgSize}
								verticalAlign="middle"
							/>
						)}
						{isTrash && <TrashIconCustom height={customSvgSize} />}
						{label && (
							<div
								style={{
									marginLeft: "5px",
									fontSize: labelSize ? labelSize : "14px",
									lineHeight: 0.9,
								}}
							>
								{label}
							</div>
						)}
					</Block>
				</StyledActionButton>
			</Tooltip>
		);
	} else {
		return (
			<StyledActionButton
				alwaysDark={alwaysDark}
				onClick={onClick}
				isHeart={isHeart}
				isComment={isComment}
				isTrash={isTrash}
				isPen={isPen}
				disabled={disabled}
				noColorDisabled={noColorDisabled}
				commented={commented}
				liked={liked}
				customMargin={customMargin}
				isSmall={
					customSvgSize === "13px" ||
					customSvgSize === "14px" ||
					customSvgSize === "15px"
				}
				isActive={isActive}
				noHover={noHover}
			>
				<Block flex alignCenter relative>
					{isHeart && (
						<DynamicHeartIcon
							height={customSvgSize}
							solid={liked}
						/>
					)}
					{isComment && (
						<DynamicCommentIcon
							height={customSvgSize}
							solid={commented}
						/>
					)}
					{isPen && (
						<Icon
							name="pen"
							variant="regular"
							height={customSvgSize}
							verticalAlign="middle"
						/>
					)}
					{isTrash && <TrashIconCustom height={customSvgSize} />}
					{label && (
						<div
							style={{
								marginLeft: "5px",
								fontSize: labelSize ? labelSize : "14px",
								lineHeight: 0.9,
							}}
						>
							{label}
						</div>
					)}
				</Block>
			</StyledActionButton>
		);
	}
};
